/**
 * Alerte — le bandeau de statut, non cliquable (lot 4 du catalogue, 01-11-2026) : « Ta session
 * X a expiré », « Les proxys sont presque épuisés », « Profil complété ». La deuxième exception
 * tolérée du CLAUDE.md (un voile et une hairline de sa couleur de SENS, sur un conteneur qui
 * n'est ni un contrôle ni une sélection), passée par cette primitive : l'icône dans sa couleur,
 * le texte blanc ou gris, une action au plus, une croix si on peut la fermer. À la place des
 * cent encarts `border-X-500/40 bg-X-500/10` écrits à la main.
 *
 *   <Alerte ton="attention" titre="Proxys presque épuisés" texte="Il en reste 3 sur 40." action={<Button size="sm">Acheter</Button>} />
 *   <Alerte ton="erreur" texte={erreur.message} onFermer={…} />
 */
import type { ReactNode } from "react";

import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from "./icons";
import { useLibelles } from "./libelles";
import { cn } from "./utils";
import "./ouverture.css";
import "./prevenir.css";

export type TonAlerte = "info" | "ok" | "attention" | "erreur";

export interface AlerteProps {
  /** Défaut `info` (gris). */
  ton?: TonAlerte;
  titre?: ReactNode;
  texte?: ReactNode;
  children?: ReactNode;
  /** À la place de l'icône du ton. */
  icone?: ReactNode;
  /** Un bouton, au plus. */
  action?: ReactNode;
  /** Rend la croix. */
  onFermer?: () => void;
  fermerLabel?: string;
  /** `sm` dans une carte, sous un champ. Défaut `md`. */
  taille?: "sm" | "md";
  /** Une BANDE pleine largeur, sans arrondi, une hairline dessous (le `Bandeau`). */
  bande?: boolean;
  className?: string;
}

const ICONES: Record<TonAlerte, ReactNode> = {
  info: <Info />,
  ok: <CheckCircle />,
  attention: <AlertTriangle />,
  erreur: <AlertCircle />,
};

export function Alerte({ ton = "info", titre, texte, children, icone, action, onFermer, fermerLabel, taille = "md", bande = false, className }: AlerteProps) {
  const libelles = useLibelles();
  return (
    <div
      role={ton === "erreur" ? "alert" : "status"}
      className={cn("pv-alerte", bande ? "pv-bande" : "pv-arrondi", className)}
      data-ton={ton}
      data-taille={taille}
      data-bande={bande || undefined}
    >
      <span className="pv-alerte-icone" aria-hidden>
        {icone ?? ICONES[ton]}
      </span>
      <div className="pv-alerte-texte">
        {titre !== undefined && titre !== null && <span className="pv-alerte-titre">{titre}</span>}
        {texte}
        {children}
      </div>
      {action && <div className="pv-alerte-action">{action}</div>}
      {onFermer && (
        <button type="button" onClick={onFermer} aria-label={fermerLabel ?? libelles.fermer} className="ov-croix ov-rond">
          <X />
        </button>
      )}
    </div>
  );
}
